import React, { useState, useEffect } from 'react';
import { 
  GraduationCap, Plus, Edit2, Trash2, Search, X, Key, Copy, Check, Loader2, Download,
  Eye, Phone, Home, Heart, FileText, Camera, Calendar, CheckCircle, AlertCircle
} from 'lucide-react';
import { studentsAPI, groupsAPI } from '../services/api';
import Pagination from '../components/Pagination';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const getTodayDate = () => {
  return new Date().toISOString().split('T')[0];
};

const residenceLabels = {
  dormitory: 'Talabalar yotoqxonasi',
  rent: 'Ijara',
  own: "O'z uyi"
};

const maritalLabels = {
  single: 'Turmush qurmagan',
  married: 'Turmush qurgan',
  divorced: 'Ajrashgan'
};

const StudentsPage = () => {
  const [students, setStudents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [editingStudent, setEditingStudent] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', middle_name: '', 
    group_id: '', phone: '', birth_date: getTodayDate()
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(20);

  useEffect(() => {
    fetchStudents();
    fetchGroups();
  }, [filterGroup, currentPage]);

  // Qidiruv o'zgarganda birinchi sahifaga qaytish
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterGroup]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage };
      if (filterGroup) params.group = filterGroup;
      if (searchTerm) params.search = searchTerm;
      const response = await studentsAPI.getAll(params);
      const data = response.data;
      
      // Pagination formatini tekshirish
      if (data.results) {
        setStudents(data.results);
        setTotalCount(data.count || 0);
      } else if (Array.isArray(data)) {
        setStudents(data);
        setTotalCount(data.length);
      } else {
        setStudents([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Talabalarni yuklashda xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await groupsAPI.getSimple();
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Guruhlarni yuklashda xato:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStudent) {
        await studentsAPI.update(editingStudent.id, formData);
      } else {
        await studentsAPI.create(formData);
      }
      fetchStudents();
      closeModal();
    } catch (error) {
      console.error('Saqlashda xato:', error);
      alert(error.response?.data?.detail || 'Xatolik yuz berdi');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Talabani o'chirishni tasdiqlaysizmi?")) {
      try {
        await studentsAPI.delete(id);
        fetchStudents();
      } catch (error) {
        console.error("O'chirishda xato:", error);
      }
    }
  };

  const handleResetPassword = async (id) => {
    if (window.confirm("Parolni yangilashni tasdiqlaysizmi?")) {
      try {
        await studentsAPI.resetPassword(id);
        fetchStudents();
      } catch (error) {
        console.error("Parolni yangilashda xato:", error);
      }
    }
  };

  const copyCredentials = (student) => {
    const text = `Login: ${student.user.username}\nParol: ${student.user.plain_password || '***'}`;
    navigator.clipboard.writeText(text);
    setCopiedId(student.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const exportToExcel = () => {
    const data = students.map((student, index) => ({
      '№': startIndex + index + 1,
      'Familiya': student.user.last_name || '',
      'Ism': student.user.first_name || '',
      'Sharifi': student.user.middle_name || '',
      'Talaba ID': student.student_id || '',
      'Guruh': student.group_display || '',
      'Telefon': student.user.phone || '',
      "Qo'shimcha telefon": student.extra_phone || '',
      'Turar joy': residenceLabels[student.residence_type] || '',
      'Turar joy manzili': student.residence_address || '',
      'Oilaviy holati': maritalLabels[student.marital_status] || '',
      'Login': student.user.username || '',
      'Parol': student.user.plain_password || '',
      "Profil to'liq": student.profile_completed ? 'Ha' : "Yo'q"
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Talabalar');
    
    ws['!cols'] = [
      { wch: 5 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
      { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
      { wch: 20 }, { wch: 30 }, { wch: 15 }, { wch: 20 },
      { wch: 12 }, { wch: 10 }
    ];
    
    XLSX.writeFile(wb, `talabalar_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const openModal = (student = null) => {
    if (student) {
      setEditingStudent(student);
      setFormData({
        first_name: student.user.first_name,
        last_name: student.user.last_name,
        middle_name: student.user.middle_name || '',
        group_id: student.group || '',
        phone: student.user.phone || '',
        birth_date: student.birth_date || getTodayDate()
      });
    } else {
      setEditingStudent(null);
      setFormData({
        first_name: '', last_name: '', middle_name: '',
        group_id: '', phone: '', birth_date: getTodayDate()
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingStudent(null);
  };

  const openDetailModal = (student) => {
    setSelectedStudent(student);
    setShowDetailModal(true);
  };

  const getFileUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('uz-UZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Pagination hisoblash
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="spinner" size={40} />
        <p>Yuklanmoqda...</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><GraduationCap size={28} /> Talabalar</h1>
          <p>Barcha talabalar ro'yxati</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={exportToExcel}>
            <Download size={20} /> Excel
          </button>
          <button className="btn btn-primary" onClick={() => openModal()}>
            <Plus size={20} /> Yangi talaba
          </button>
        </div>
      </header>

      {/* Filters */}
      <div className="filters-row">
        <div className="search-bar">
          <Search size={20} />
          <input 
            type="text" 
            placeholder="Talabani qidirish... (Enter bosing)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setCurrentPage(1);
                fetchStudents();
              }
            }}
          />
          <button 
            className="btn btn-sm btn-primary" 
            onClick={() => { setCurrentPage(1); fetchStudents(); }}
            style={{ marginLeft: '8px' }}
          >
            Qidirish
          </button>
        </div>
        <select 
          className="filter-select"
          value={filterGroup}
          onChange={(e) => setFilterGroup(e.target.value)}
        >
          <option value="">Barcha guruhlar</option>
          {groups.map(group => (
            <option key={group.id} value={group.id}>
              {group.display_name}
            </option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '16px',
        padding: '12px 16px',
        background: '#f8fafc',
        borderRadius: '8px'
      }}>
        <span style={{ color: '#64748b' }}>
          Jami: <strong style={{ color: '#1e293b' }}>{totalCount}</strong> ta talaba
        </span>
        <span style={{ color: '#64748b' }}>
          Sahifa: <strong style={{ color: '#1e293b' }}>{currentPage}</strong> / {totalPages || 1}
        </span>
      </div>

      {/* Students Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>№</th>
              <th>F.I.Sh</th>
              <th>Talaba ID</th>
              <th>Guruh</th>
              <th>Telefon</th>
              <th>Profil</th>
              <th>Login / Parol</th>
              <th>Amallar</th>
            </tr>
          </thead>
          <tbody>
            {students.map((student, index) => (
              <tr key={student.id}>
                <td>{startIndex + index + 1}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {student.photo_url ? (
                      <img 
                        src={getFileUrl(student.photo_url)} 
                        alt="" 
                        style={{ 
                          width: '36px', 
                          height: '36px', 
                          borderRadius: '50%', 
                          objectFit: 'cover' 
                        }} 
                      />
                    ) : (
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: '#e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#64748b'
                      }}>
                        {student.user.first_name?.[0]}{student.user.last_name?.[0]}
                      </div>
                    )}
                    <strong>{student.user.full_name}</strong>
                  </div>
                </td>
                <td>
                  <span className="badge badge-secondary">{student.student_id}</span>
                </td>
                <td>{student.group_display || '-'}</td>
                <td>{student.user.phone || '-'}</td>
                <td>
                  {student.profile_completed ? (
                    <span style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '4px', 
                      color: '#10b981',
                      fontSize: '13px'
                    }}>
                      <CheckCircle size={16} /> To'liq
                    </span>
                  ) : (
                    <span style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '4px', 
                      color: '#f59e0b',
                      fontSize: '13px'
                    }}>
                      <AlertCircle size={16} /> To'liq emas
                    </span>
                  )}
                </td>
                <td>
                  <div className="credentials-cell">
                    <code className="login-code">{student.user.username}</code>
                    <code className="password-code">{student.user.plain_password || '***'}</code>
                    <button 
                      className="btn-copy"
                      onClick={() => copyCredentials(student)}
                      title="Nusxalash"
                    >
                      {copiedId === student.id ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="btn-icon"
                      style={{ background: '#dbeafe', color: '#2563eb' }}
                      onClick={() => openDetailModal(student)}
                      title="Profilni ko'rish"
                    >
                      <Eye size={18} />
                    </button>
                    <button 
                      className="btn-icon key" 
                      onClick={() => handleResetPassword(student.id)}
                      title="Parolni yangilash"
                    >
                      <Key size={18} />
                    </button>
                    <button className="btn-icon edit" onClick={() => openModal(student)}>
                      <Edit2 size={18} />
                    </button>
                    <button className="btn-icon delete" onClick={() => handleDelete(student.id)}>
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {students.length === 0 && (
          <div className="empty-state">
            <GraduationCap size={48} />
            <p>Talabalar topilmadi</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
      />

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingStudent ? "Talabani tahrirlash" : "Yangi talaba"}</h2>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Familiya *</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    placeholder="Familiya"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Ism *</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    placeholder="Ism"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Sharifi</label>
                  <input
                    type="text"
                    value={formData.middle_name}
                    onChange={(e) => setFormData({...formData, middle_name: e.target.value})}
                    placeholder="Sharifi"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Guruh *</label>
                  <select
                    value={formData.group_id}
                    onChange={(e) => setFormData({...formData, group_id: e.target.value})}
                    required
                  >
                    <option value="">Guruhni tanlang</option>
                    {groups.map(group => (
                      <option key={group.id} value={group.id}>
                        {group.display_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Telefon</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+998 90 123 45 67"
                  />
                </div>
                <div className="form-group">
                  <label>Tug'ilgan sana</label>
                  <input
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
                  />
                </div>
              </div>
              {!editingStudent && (
                <div className="info-box">
                  <p>💡 Login va parol avtomatik yaratiladi va jadvalda ko'rsatiladi</p>
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Bekor qilish
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingStudent ? "Saqlash" : "Yaratish"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Student Detail Modal */}
      {showDetailModal && selectedStudent && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div 
            className="modal" 
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}
          >
            <div className="modal-header">
              <h2>Talaba profili</h2>
              <button className="btn-close" onClick={() => setShowDetailModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              {/* Asosiy ma'lumotlar */}
              <div style={{ 
                display: 'flex', 
                gap: '24px', 
                marginBottom: '24px',
                padding: '20px',
                background: '#f8fafc',
                borderRadius: '12px'
              }}>
                {selectedStudent.photo_url ? (
                  <img 
                    src={getFileUrl(selectedStudent.photo_url)} 
                    alt="" 
                    style={{ 
                      width: '120px', 
                      height: '120px', 
                      borderRadius: '12px', 
                      objectFit: 'cover',
                      border: '3px solid #10b981'
                    }} 
                  />
                ) : (
                  <div style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '12px',
                    background: '#e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '36px',
                    fontWeight: '600',
                    color: '#64748b'
                  }}>
                    {selectedStudent.user.first_name?.[0]}{selectedStudent.user.last_name?.[0]}
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 8px', fontSize: '20px' }}>{selectedStudent.user.full_name}</h3>
                  <p style={{ margin: '0 0 4px', color: '#64748b' }}>
                    <strong>Talaba ID:</strong> {selectedStudent.student_id}
                  </p>
                  <p style={{ margin: '0 0 4px', color: '#64748b' }}>
                    <strong>Guruh:</strong> {selectedStudent.group_display || '-'}
                  </p>
                  <p style={{ margin: '0 0 4px', color: '#64748b' }}>
                    <strong>Tug'ilgan sana:</strong> {formatDate(selectedStudent.birth_date)}
                  </p>
                  {selectedStudent.profile_completed ? (
                    <span style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      marginTop: '8px',
                      padding: '6px 12px',
                      background: '#d1fae5',
                      color: '#059669',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}>
                      <CheckCircle size={16} /> Profil to'liq
                    </span>
                  ) : (
                    <span style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      marginTop: '8px',
                      padding: '6px 12px',
                      background: '#fef3c7',
                      color: '#d97706',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}>
                      <AlertCircle size={16} /> Profil to'liq emas
                    </span>
                  )}
                </div>
              </div>

              {/* Bog'lanish ma'lumotlari */}
              <div className="card" style={{ margin: '0 0 16px' }}>
                <div className="card-header"><h3><Phone size={18} /> Bog'lanish</h3></div>
                <div className="card-body">
                  <div className="detail-row">
                    <label>Telefon</label>
                    <span>{selectedStudent.user.phone || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <label>Qo'shimcha telefon</label>
                    <span>{selectedStudent.extra_phone || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Turar joy */}
              <div className="card" style={{ margin: '0 0 16px' }}>
                <div className="card-header"><h3><Home size={18} /> Turar joy</h3></div>
                <div className="card-body">
                  <div className="detail-row">
                    <label>Turar joy turi</label>
                    <span>{residenceLabels[selectedStudent.residence_type] || '-'}</span>
                  </div>
                  <div className="detail-row">
                    <label>Manzil</label>
                    <span>{selectedStudent.residence_address || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Shaxsiy ma'lumotlar */}
              <div className="card" style={{ margin: '0 0 16px' }}>
                <div className="card-header"><h3><Heart size={18} /> Shaxsiy</h3></div>
                <div className="card-body">
                  <div className="detail-row">
                    <label>Oilaviy holati</label>
                    <span>{maritalLabels[selectedStudent.marital_status] || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Hujjatlar */}
              <div className="card" style={{ margin: '0' }}>
                <div className="card-header"><h3><FileText size={18} /> Hujjatlar</h3></div>
                <div className="card-body">
                  <div className="detail-row">
                    <label><Camera size={16} /> Talaba rasmi</label>
                    {selectedStudent.photo_url ? (
                      <a 
                        href={getFileUrl(selectedStudent.photo_url)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#2563eb' }}
                      >
                        Ko'rish →
                      </a>
                    ) : (
                      <span style={{ color: '#ef4444' }}>Yuklanmagan</span>
                    )}
                  </div>
                  <div className="detail-row">
                    <label><FileText size={16} /> Attestat</label>
                    {selectedStudent.attestation_url ? (
                      <a 
                        href={getFileUrl(selectedStudent.attestation_url)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#2563eb' }}
                      >
                        Ko'rish →
                      </a>
                    ) : (
                      <span style={{ color: '#ef4444' }}>Yuklanmagan</span>
                    )}
                  </div>
                  <div className="detail-row">
                    <label><FileText size={16} /> Obektivka</label>
                    {selectedStudent.objective_url ? (
                      <a 
                        href={getFileUrl(selectedStudent.objective_url)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#2563eb' }}
                      >
                        Ko'rish →
                      </a>
                    ) : (
                      <span style={{ color: '#ef4444' }}>Yuklanmagan</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowDetailModal(false)}
              >
                Yopish
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentsPage;
